/**
 * Backtest panel (Strategy Tester) — Phase 6a-2 commit 5 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A50, v2.4/R20). Bodies
 * moved VERBATIM from static/index.html; the only edits are the approved
 * injected-context substitutions (ctx.*) and the bundle-internal
 * formatIndicatorName import.
 *
 * The injected context is FROZEN at ten entries (v2.4, Option B) — see the
 * blueprint's R20 row. setResults/setRunning preserve two write-only state
 * fields by explicit ruling: no behaviour removal during Phase 6.
 *
 * The panel owns its #backtest-panel / #bt-* DOM subtree (the markup stays in
 * index.html). Page capabilities arrive ONLY via the context; the one
 * page-facing capability flowing the other way is applyEquityChartTheme
 * (H25 — applyTheme decides WHEN to re-theme, the panel owns HOW).
 */

import { formatIndicatorName } from "../domain/format";

export interface BacktestPanelContext {
  /** Page-owned auth header builder (A5). */
  authHeaders(extra?: Record<string, string>): Record<string, string>;
  /** Page-owned 401/banner path (R20). */
  handleAuthFailure(resp: { status: number }): void;
  /** Page-local DOM-based escaper (R19). */
  escapeHtml(s: unknown): string;
  /** Bridged reads — resolve page-side at call time (H1). */
  getSymbol(): string;
  getTimeframe(): string;
  /** H23: writes state.backtestChartMarkers + backtestMarkersVisible=true and
   * calls applyMarkers() — the A24 merge stays page-owned. */
  setMarkers(markers: unknown[]): void;
  /** H23: writes state.backtestMarkersVisible and calls applyMarkers(). */
  setMarkersVisible(visible: boolean): void;
  /** Option B (v2.4): preserves the write-only state.backtestResults. */
  setResults(data: unknown): void;
  /** Option B (v2.4): preserves the write-only state.backtestRunning. */
  setRunning(running: boolean): void;
  /** The page's LightweightCharts handle (incl. its fallback-guard semantics). */
  LC: any;
}

let ctx: BacktestPanelContext;

// Panel-private runtime state (moved from the page's module scope, L2829–2831).
let backtestEquityChart: any = null;
let backtestEquitySeries: any = null;
let backtestStrategiesLoaded = false;

function openBacktestPanel(): void {
  const panel = document.getElementById('backtest-panel')!;
  panel.style.display = 'flex';
  if (!backtestStrategiesLoaded) loadBacktestStrategies();
  const symEl = document.getElementById('bt-symbol') as HTMLInputElement | null;
  const tfEl = document.getElementById('bt-timeframe') as HTMLSelectElement | null;
  if (symEl && !symEl.value) symEl.value = ctx.getSymbol();
  // <select> always reports a truthy .value (defaults to its first
  // <option>), so this must sync unconditionally rather than the
  // empty-string guard used for the text/date fields below — otherwise
  // it would silently stay on whatever option happens to be first in
  // the markup instead of following the chart's actual timeframe.
  if (tfEl) {
    const candidateTf = ['1m','3m','5m','15m','1h','1D'].includes(ctx.getTimeframe()) ? ctx.getTimeframe() : '3m';
    tfEl.value = candidateTf;
  }
  const startEl = document.getElementById('bt-start-date') as HTMLInputElement | null;
  const endEl = document.getElementById('bt-end-date') as HTMLInputElement | null;
  if (startEl && !startEl.value) {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 86400000);
    endEl!.value = end.toISOString().slice(0, 10);
    startEl.value = start.toISOString().slice(0, 10);
  }
}

function closeBacktestPanel(): void {
  document.getElementById('backtest-panel')!.style.display = 'none';
}

async function loadBacktestStrategies(): Promise<void> {
  const sel = document.getElementById('bt-strategy')!;
  try {
    const resp = await fetch('/api/backtest/strategies');
    const data = await resp.json();
    const strategies = data.strategies || [];
    sel.innerHTML = strategies.map((s: string) => `<option value="${ctx.escapeHtml(s)}">${ctx.escapeHtml(formatIndicatorName(s))}</option>`).join('');
    backtestStrategiesLoaded = true;
    if (!strategies.length) setBacktestStatus('No strategies with known entry signals are loaded.', true);
  } catch (err) {
    console.error('Failed to load backtest strategies:', err);
    setBacktestStatus('Failed to load strategy list.', true);
  }
}

function setBacktestStatus(msg: string, isError?: boolean): void {
  const el = document.getElementById('bt-status')!;
  el.textContent = msg || '';
  el.classList.toggle('error', !!isError);
}

function switchBacktestTab(tabName: string): void {
  document.querySelectorAll('.bt-tab-btn').forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset.btTab === tabName));
  document.querySelectorAll('.bt-tab-panel').forEach(p => { (p as HTMLElement).style.display = ((p as HTMLElement).dataset.btPanel === tabName) ? 'block' : 'none'; });
  if (tabName === 'overview' && backtestEquityChart) {
    // Chart was built while its container was hidden by a sibling tab;
    // give the layout a tick to settle before asking autoSize to fit.
    setTimeout(() => { backtestEquityChart.timeScale().fitContent(); }, 0);
  }
}

async function runBacktest(): Promise<void> {
  const runBtn = document.getElementById('bt-run-btn') as HTMLButtonElement;
  const symbol = (document.getElementById('bt-symbol') as HTMLInputElement).value.trim();
  const timeframe = (document.getElementById('bt-timeframe') as HTMLSelectElement).value;
  const plugin = (document.getElementById('bt-strategy') as HTMLSelectElement).value;
  const startDate = (document.getElementById('bt-start-date') as HTMLInputElement).value;
  const endDate = (document.getElementById('bt-end-date') as HTMLInputElement).value;
  if (!symbol) { setBacktestStatus('Symbol is required.', true); return; }
  if (!plugin) { setBacktestStatus('No strategy selected.', true); return; }
  if (!startDate || !endDate) { setBacktestStatus('Start and end date are required.', true); return; }

  const payload = {
    symbol, timeframe, plugin,
    start_date: startDate, end_date: endDate,
    position_size: parseFloat((document.getElementById('bt-position-size') as HTMLInputElement).value) || 0,
    position_size_type: (document.getElementById('bt-position-size-type') as HTMLSelectElement).value,
    starting_capital: parseFloat((document.getElementById('bt-starting-capital') as HTMLInputElement).value) || 100000,
    sl_pct: parseFloat((document.getElementById('bt-sl-pct') as HTMLInputElement).value) || 0,
    tp_pct: parseFloat((document.getElementById('bt-tp-pct') as HTMLInputElement).value) || 0,
    trailing_pct: parseFloat((document.getElementById('bt-trailing-pct') as HTMLInputElement).value) || 0,
    max_holding_bars: parseInt((document.getElementById('bt-max-holding-bars') as HTMLInputElement).value, 10) || 0,
    slippage_pct: parseFloat((document.getElementById('bt-slippage-pct') as HTMLInputElement).value) || 0,
    commission_pct: parseFloat((document.getElementById('bt-commission-pct') as HTMLInputElement).value) || 0,
  };

  ctx.setRunning(true);
  runBtn.disabled = true;
  setBacktestStatus('Running…', false);
  try {
    const resp = await fetch('/api/backtest', {
      method: 'POST',
      headers: ctx.authHeaders({'Content-Type': 'application/json'}),
      body: JSON.stringify(payload),
    });
    const data = await resp.json();
    if (!resp.ok) {
      ctx.handleAuthFailure(resp);
      setBacktestStatus(data.detail || 'Backtest failed.', true);
      return;
    }
    ctx.setResults(data);
    setBacktestStatus(`${data.candle_count} candles, ${data.signal_count} signals, ${data.stats.total_trades} trades`, false);
    renderBacktestResults(data);
    switchBacktestTab('overview');
    setBacktestChartMarkers(data.trades);
  } catch (err) {
    console.error('Backtest request failed:', err);
    setBacktestStatus('Backtest request failed — see console.', true);
  } finally {
    ctx.setRunning(false);
    runBtn.disabled = false;
  }
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '--';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}
export function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '--';
  return n.toFixed(2) + '%';
}
export function fmtRatio(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '--';
  return n.toFixed(2);
}
export function statCard(label: string, value: unknown, cls?: string): string {
  return `<div class="bt-stat-card"><div class="bt-stat-label">${label}</div><div class="bt-stat-value ${cls || ''}">${value}</div></div>`;
}
// NC-21: one <tr> of the trades table. direction and exit_reason are
// server-provided strings that go into innerHTML, so they are escaped via the
// injected `esc` (the numeric/time/price cells emit from toFixed/toLocaleString
// and carry no HTML-significant characters). Pure and escaper-injected so the
// XSS pin can exercise it without a DOM (the DOM-based page escaper can't run
// under vitest). Escape the interpolated fields, not the authored markup (TRAP 2).
export function tradeRowHtml(t: any, i: number, esc: (s: unknown) => string): string {
  const pnlCls = t.pnl > 0 ? 'pos' : (t.pnl < 0 ? 'neg' : '');
  return `<tr>
        <td>${i + 1}</td>
        <td>${esc(t.direction)}</td>
        <td>${fmtBacktestTime(t.entry_time)}</td>
        <td>${fmtRatio(t.entry_price)}</td>
        <td>${fmtBacktestTime(t.exit_time)}</td>
        <td>${fmtRatio(t.exit_price)}</td>
        <td>${fmtRatio(t.qty)}</td>
        <td class="${pnlCls}">${fmtMoney(t.pnl)}</td>
        <td class="${pnlCls}">${fmtPct(t.pnl_pct)}</td>
        <td>${esc(t.exit_reason)}</td>
        <td>${t.bars_held}</td>
      </tr>`;
}
export function fmtBacktestTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-IN', { hour12: false, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function renderBacktestResults(data: any): void {
  const stats = data.stats;

  const ov = document.getElementById('bt-overview-stats')!;
  ov.innerHTML = [
    statCard('Total Trades', ctx.escapeHtml(String(stats.total_trades))),
    statCard('Win Rate', fmtPct(stats.win_rate)),
    statCard('Total P&amp;L', fmtMoney(stats.total_pnl), stats.total_pnl > 0 ? 'pos' : (stats.total_pnl < 0 ? 'neg' : '')),
    statCard('Total P&amp;L %', fmtPct(stats.total_pnl_pct), stats.total_pnl_pct > 0 ? 'pos' : (stats.total_pnl_pct < 0 ? 'neg' : '')),
    statCard('Profit Factor', fmtRatio(stats.profit_factor)),
    statCard('Max Drawdown', fmtPct(stats.max_drawdown_pct)),
  ].join('');
  renderEquityCurveChart(data.equity_curve);

  const perf = document.getElementById('bt-performance-stats')!;
  perf.innerHTML = [
    statCard('Avg Win', fmtMoney(stats.avg_win), 'pos'),
    statCard('Avg Loss', fmtMoney(stats.avg_loss), 'neg'),
    statCard('Largest Win', fmtMoney(stats.largest_win), 'pos'),
    statCard('Largest Loss', fmtMoney(stats.largest_loss), 'neg'),
    statCard('Max Consecutive Wins', ctx.escapeHtml(String(stats.max_consecutive_wins))),
    statCard('Max Consecutive Losses', ctx.escapeHtml(String(stats.max_consecutive_losses))),
    statCard('Avg Bars Held', fmtRatio(stats.avg_bars_held)),
    statCard('Signal Count', ctx.escapeHtml(String(data.signal_count))),
    statCard('Candle Count', ctx.escapeHtml(String(data.candle_count))),
  ].join('');

  // avg_win/avg_loss are already the honest per-trade means computed
  // server-side (routers/backtest.py's compute_stats) — this ratio is
  // just their magnitude comparison, not a new statistic.
  const riskReward = (stats.avg_win && stats.avg_loss) ? Math.abs(stats.avg_win / stats.avg_loss) : null;
  const risk = document.getElementById('bt-risk-stats')!;
  risk.innerHTML = [
    statCard('Max Drawdown', fmtMoney(stats.max_drawdown)),
    statCard('Max Drawdown %', fmtPct(stats.max_drawdown_pct)),
    statCard('Sharpe (per trade)', fmtRatio(stats.sharpe_per_trade)),
    statCard('Sortino (per trade)', fmtRatio(stats.sortino_per_trade)),
    statCard('Avg Win / Avg Loss', fmtRatio(riskReward)),
  ].join('');

  const tbody = document.querySelector('#bt-trades-table tbody')!;
  if (!data.trades.length) {
    tbody.innerHTML = `<tr><td colspan="11" class="bt-empty-state">No trades in this range.</td></tr>`;
  } else {
    tbody.innerHTML = data.trades.map((t: any, i: number) => tradeRowHtml(t, i, ctx.escapeHtml)).join('');
  }
}

function renderEquityCurveChart(equityCurve: Array<{ time: number; equity: number }>): void {
  const container = document.getElementById('bt-equity-chart');
  if (!container) return;
  if (!backtestEquityChart) {
    const savedTheme = localStorage.getItem('theme');
    const isLight = savedTheme === 'light';
    backtestEquityChart = ctx.LC.createChart(container, {
      layout: {
        background: { type: 'solid', color: isLight ? '#F5F7FA' : '#0F1115' },
        textColor: isLight ? '#141A22' : '#E7EBF0',
      },
      grid: {
        vertLines: { color: isLight ? '#E2E7EE' : '#23282f' },
        horzLines: { color: isLight ? '#E2E7EE' : '#23282f' },
      },
      timeScale: { timeVisible: true, secondsVisible: false },
      autoSize: true,
    });
    backtestEquitySeries = backtestEquityChart.addLineSeries({ color: '#4DA3E0', lineWidth: 2 });
  }
  // equity_curve carries one point per bar; duplicate `time`s (shouldn't
  // happen, but candle data has had corruption surprises this session)
  // would make lightweight-charts throw and blank the whole chart, so
  // guard defensively rather than trust the backend series is clean.
  const seen = new Set<number>();
  const points: Array<{ time: number; value: number }> = [];
  for (const pt of (equityCurve || [])) {
    if (seen.has(pt.time)) continue;
    seen.add(pt.time);
    points.push({ time: pt.time, value: pt.equity });
  }
  backtestEquitySeries.setData(points);
  backtestEquityChart.timeScale().fitContent();
}

// Builds candleSeries-marker objects for every trade's entry and exit so
// a backtest run can be visually cross-checked against price action —
// entry arrows point in the trade direction (blue, direction-neutral
// color since the direction is already conveyed by the arrow), exit
// arrows are colored by outcome (green profit / red loss). Stored
// separately from state.markers (the plugin-marker system) and merged
// in by applyMarkers() — which stays page-side; the hand-off happens
// through ctx.setMarkers (H23).
function setBacktestChartMarkers(trades: any[]): void {
  const chartMarkers = [];
  for (const t of trades) {
    const isLong = t.direction === 'long';
    chartMarkers.push({
      time: t.entry_time,
      position: isLong ? 'belowBar' : 'aboveBar',
      shape: isLong ? 'arrowUp' : 'arrowDown',
      color: '#2196F3',
      text: 'E',
    });
    chartMarkers.push({
      time: t.exit_time,
      position: isLong ? 'aboveBar' : 'belowBar',
      shape: isLong ? 'arrowDown' : 'arrowUp',
      color: t.pnl > 0 ? '#2DBE8E' : '#F06A6A',
      text: 'X',
    });
  }
  const toggle = document.getElementById('bt-markers-toggle');
  const cb = document.getElementById('bt-show-markers-cb') as HTMLInputElement | null;
  if (toggle) toggle.style.display = 'flex';
  if (cb) cb.checked = true;
  // Page-side: writes the two H23 state fields and calls applyMarkers()
  // (kept LAST, matching the original statement order's final call).
  ctx.setMarkers(chartMarkers);
}

/** H25: applyTheme (page) delegates the equity chart's re-theming here —
 * the handle is panel-private now. Guard semantics verbatim from the page. */
export function applyEquityChartTheme(options: unknown): void {
  if (backtestEquityChart) {
    try { backtestEquityChart.applyOptions(options); } catch (e) {}
  }
}

export function wireBacktestPanel(context: BacktestPanelContext): void {
  ctx = context;
  const btn = document.getElementById('backtest-btn');
  if (btn) btn.addEventListener('click', () => {
    const panel = document.getElementById('backtest-panel')!;
    if (panel.style.display === 'none') openBacktestPanel();
    else closeBacktestPanel();
  });
  const closeBtn = document.getElementById('bt-close');
  if (closeBtn) closeBtn.addEventListener('click', closeBacktestPanel);
  const runBtn = document.getElementById('bt-run-btn');
  if (runBtn) runBtn.addEventListener('click', runBacktest);
  document.querySelectorAll('.bt-tab-btn').forEach(b => {
    b.addEventListener('click', () => switchBacktestTab((b as HTMLElement).dataset.btTab!));
  });
  const markersCb = document.getElementById('bt-show-markers-cb') as HTMLInputElement | null;
  if (markersCb) markersCb.addEventListener('change', () => {
    ctx.setMarkersVisible(markersCb.checked);
  });
}
